import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography } from '@mui/material';

import {
    ContextActionType,
    FlexyTabs,
    OfferDashboardSkeleton,
    Scrollbar,
    SkeletonLoader,
    useDumbActionsContext,
} from '@europrocurement/flexy-components';
import { setXIdSociete } from '@europrocurement/l2d-domain';
import useLoadStore, { LoadableCollection } from '@europrocurement/l2d-modules/hooks/useLoadStore';
import useLoadLibArticle from '@europrocurement/l2d-modules/hooks/useLoadLibArticle';
import { GrilleTarifaire } from '@europrocurement/l2d-modules/modules/GrilleTarifaire/page/GrilleTarifaire';

import useOfferCache from './cache/useOfferCache';
import { PackagePanel } from './Package';
import { ArticlePanel } from './Article';
import { CatalogPanel } from './Catalog';
import { OptionPanel } from './Option';
import { OfferAdditionalFieldPanel } from './AdditionalField';
import { DocumentPanel } from './Document';
import { GroupPanel } from './Group';
import { OfferPanel } from './Offer';

const L2D_SOCIETE_ID = 12;

const COLLECTIONS_TO_FETCH: LoadableCollection[] = [
    'vats',
    'domains',
    'regleDeCalculs',
    'invoicingCategories',
    'formalityCategories',
    'articlesCategories',
    'origines',
];

/**
 * Dashboard de gestion des offres marketing.
 * Contient plusieurs tabs pour gérer différentes entités (catalogues, packages, articles, etc.)
 */
const OfferDashboard: React.FunctionComponent = function () {
    const dispatch = useDispatch();

    useOfferCache();
    useLoadLibArticle();

    const { getLoadingStatus } = useLoadStore(COLLECTIONS_TO_FETCH);

    const loadingStatus = getLoadingStatus();
    const isLoading = loadingStatus === 'loading' || loadingStatus === 'idle';

    useEffect(() => {
        dispatch(setXIdSociete(L2D_SOCIETE_ID));
    }, [dispatch]);

    const contextActions: ContextActionType[] = useMemo(() => [], []);
    useDumbActionsContext(contextActions);

    const tabs = useMemo(
        () => [
            {
                tabTitle: 'Catalogues',
                tabPanel: <CatalogPanel />,
                tabName: 'catalogue',
            },
            {
                tabTitle: 'Packages',
                tabPanel: <PackagePanel />,
                tabName: 'package',
            },
            {
                tabTitle: 'Articles',
                tabPanel: <ArticlePanel />,
                tabName: 'article',
            },
            {
                tabTitle: 'Options',
                tabPanel: <OptionPanel />,
                tabName: 'option',
            },
            {
                tabTitle: 'Champs complémentaires',
                tabPanel: <OfferAdditionalFieldPanel />,
                tabName: 'champComplementaire',
            },
            {
                tabTitle: 'Documents',
                tabPanel: <DocumentPanel />,
                tabName: 'document',
            },
            {
                tabTitle: 'Groupements',
                tabPanel: <GroupPanel />,
                tabName: 'groupement',
            },
            {
                tabTitle: 'Offres',
                tabPanel: <OfferPanel />,
                tabName: 'offre',
            },
            {
                tabTitle: 'Grille tarifaire',
                tabPanel: (
                    <Scrollbar
                        style={{
                            maxHeight: `700px`,
                        }}
                    >
                        <GrilleTarifaire
                            showOrigineFilter
                            showNumberCatalogue
                            showOffer
                            showElement
                            showTTC={false}
                            showAction
                            surchargeAL
                            surchargeFD
                            surchargeHonoraire={false}
                            waitPrescripteur={false}
                        />
                    </Scrollbar>
                ),
                tabName: 'grilleTarifaire',
            },
        ],
        [],
    );

    return (
        <SkeletonLoader
            isLoading={isLoading}
            skeleton={<OfferDashboardSkeleton />}
        >
            <Box sx={{ overflow: 'hidden' }}>
                <Typography
                    variant="h1"
                    sx={{ marginBottom: 2 }}
                >
                    Gestion des offres marketing
                </Typography>

                <FlexyTabs
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '15px',
                    }}
                    tabs={tabs}
                />
            </Box>
        </SkeletonLoader>
    );
};

export default OfferDashboard;
